import { useState, useEffect } from 'react';
import {
  CreditCardIcon,
  CheckCircleIcon,
  XCircleIcon,
  ArrowTopRightOnSquareIcon,
  BanknotesIcon,
  ClockIcon,
  UserGroupIcon,
  PlusIcon,
} from '@heroicons/react/24/outline';
import { useAgency } from '../context/AgencyContext';
import {
  getConnectStatus,
  createConnectAccount,
  getConnectOnboardingLink,
  getConnectLoginLink,
  getClientPayments,
  getClientSubscriptions,
  createClientInvoice,
  ConnectStatus,
} from '../services/api';

function formatPrice(cents: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(cents / 100);
}

function formatDate(timestamp: number): string {
  return new Date(timestamp * 1000).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

export default function ClientBillingPage() {
  const { currentAgency, businesses, loading: agencyLoading } = useAgency();
  const [connectStatus, setConnectStatus] = useState<ConnectStatus | null>(null);
  const [payments, setPayments] = useState<any[]>([]);
  const [subscriptions, setSubscriptions] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Create invoice form state
  const [showInvoiceForm, setShowInvoiceForm] = useState(false);
  const [selectedBusinessId, setSelectedBusinessId] = useState('');
  const [invoiceAmount, setInvoiceAmount] = useState('');
  const [invoiceDescription, setInvoiceDescription] = useState('');
  const [paymentType, setPaymentType] = useState<'one_time' | 'subscription'>('one_time');
  const [billingInterval, setBillingInterval] = useState<'month' | 'year'>('month');
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  useEffect(() => {
    if (!currentAgency) return;

    async function fetchData() {
      setLoading(true);
      setError(null);
      try {
        const { status } = await getConnectStatus(currentAgency!.id);
        setConnectStatus(status);

        if (status?.chargesEnabled) {
          const [paymentsData, subsData] = await Promise.all([
            getClientPayments(currentAgency!.id, 10),
            getClientSubscriptions(currentAgency!.id, 10),
          ]);
          setPayments(paymentsData.payments);
          setSubscriptions(subsData.subscriptions);
        }
      } catch (err: any) {
        setError(err.message || 'Failed to load billing data');
      } finally {
        setLoading(false);
      }
    }

    fetchData();
  }, [currentAgency]);

  const handleSetupConnect = async () => {
    if (!currentAgency) return;

    setActionLoading('setup');
    setError(null);
    try {
      // First create the account if it doesn't exist
      if (!connectStatus?.accountId) {
        await createConnectAccount(currentAgency.id);
      }

      // Then get the onboarding link
      const baseUrl = window.location.origin;
      const { url } = await getConnectOnboardingLink(
        currentAgency.id,
        `${baseUrl}/client-billing?refresh=true`,
        `${baseUrl}/client-billing?success=true`
      );
      window.location.href = url;
    } catch (err: any) {
      setError(err.message || 'Failed to set up Stripe Connect');
    } finally {
      setActionLoading(null);
    }
  };

  const handleOpenDashboard = async () => {
    if (!currentAgency) return;

    setActionLoading('dashboard');
    try {
      const { url } = await getConnectLoginLink(currentAgency.id);
      window.open(url, '_blank');
    } catch (err: any) {
      setError(err.message || 'Failed to open Stripe dashboard');
    } finally {
      setActionLoading(null);
    }
  };

  const handleCreateInvoice = async () => {
    if (!currentAgency || !selectedBusinessId || !invoiceAmount) return;
    if (paymentType === 'one_time' && !invoiceDescription) return;

    const amountCents = Math.round(parseFloat(invoiceAmount) * 100);
    if (isNaN(amountCents) || amountCents <= 0) {
      setError('Please enter a valid amount');
      return;
    }

    setActionLoading('invoice');
    setError(null);
    setSuccessMessage(null);
    try {
      const description = paymentType === 'subscription'
        ? `AI Receptionist Service - ${billingInterval}ly subscription`
        : invoiceDescription;

      await createClientInvoice(currentAgency.id, selectedBusinessId, {
        type: paymentType,
        amountCents,
        description,
        interval: paymentType === 'subscription' ? billingInterval : undefined,
      });

      // Get the business name for the success message
      const business = businesses.find(b => b.id === selectedBusinessId);
      setSuccessMessage(`Invoice created for ${business?.name || 'client'}. They will see it in their billing portal.`);

      // Reset form
      setShowInvoiceForm(false);
      setSelectedBusinessId('');
      setInvoiceAmount('');
      setInvoiceDescription('');
      setPaymentType('one_time');
      setBillingInterval('month');
    } catch (err: any) {
      setError(err.message || 'Failed to create invoice');
    } finally {
      setActionLoading(null);
    }
  };

  if (agencyLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
      </div>
    );
  }

  if (!currentAgency) {
    return (
      <div className="text-center py-12">
        <h3 className="text-lg font-semibold text-gray-900">No Agency Selected</h3>
        <p className="mt-2 text-sm text-gray-500">
          Select an agency from the dropdown above.
        </p>
      </div>
    );
  }

  // Check for success/refresh query params
  const urlParams = new URLSearchParams(window.location.search);
  const showSuccess = urlParams.get('success') === 'true';

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">Client Billing</h1>
        <p className="mt-1 text-sm text-gray-500">
          Manage payments from your clients through Stripe Connect
        </p>
      </div>

      {showSuccess && (
        <div className="mb-6 rounded-md bg-green-50 p-4">
          <div className="flex">
            <CheckCircleIcon className="h-5 w-5 text-green-400" />
            <div className="ml-3">
              <p className="text-sm font-medium text-green-800">
                Stripe Connect setup complete! You can now accept payments from clients.
              </p>
            </div>
          </div>
        </div>
      )}

      {successMessage && (
        <div className="mb-6 rounded-md bg-green-50 p-4">
          <div className="flex">
            <CheckCircleIcon className="h-5 w-5 text-green-400" />
            <div className="ml-3">
              <p className="text-sm font-medium text-green-800">
                {successMessage}
              </p>
            </div>
          </div>
        </div>
      )}

      {error && (
        <div className="mb-6 rounded-md bg-red-50 p-4">
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
        </div>
      ) : (
        <>
          {/* Connect Status Card */}
          <div className="mb-8 bg-white shadow rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <div className="sm:flex sm:items-center sm:justify-between">
                <div>
                  <h3 className="text-lg font-medium leading-6 text-gray-900">
                    Stripe Connect Status
                  </h3>
                  <div className="mt-2 flex items-center gap-3">
                    {connectStatus?.chargesEnabled ? (
                      <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2.5 py-0.5 text-sm font-medium text-green-800">
                        <CheckCircleIcon className="h-4 w-4" />
                        Active
                      </span>
                    ) : connectStatus?.accountId ? (
                      <span className="inline-flex items-center gap-1 rounded-full bg-yellow-100 px-2.5 py-0.5 text-sm font-medium text-yellow-800">
                        <ClockIcon className="h-4 w-4" />
                        Setup Incomplete
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2.5 py-0.5 text-sm font-medium text-gray-800">
                        <XCircleIcon className="h-4 w-4" />
                        Not Connected
                      </span>
                    )}
                  </div>
                  {connectStatus?.chargesEnabled && (
                    <p className="mt-2 text-sm text-gray-500">
                      Your Stripe account is ready to accept payments from clients.
                    </p>
                  )}
                </div>
                <div className="mt-4 sm:mt-0 flex gap-3">
                  {connectStatus?.chargesEnabled ? (
                    <>
                      <button
                        type="button"
                        onClick={() => setShowInvoiceForm(true)}
                        className="inline-flex items-center gap-2 rounded-md bg-primary-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary-500"
                      >
                        <PlusIcon className="h-4 w-4" />
                        Create Invoice
                      </button>
                      <button
                        type="button"
                        onClick={handleOpenDashboard}
                        disabled={actionLoading === 'dashboard'}
                        className="inline-flex items-center gap-2 rounded-md bg-white px-4 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:opacity-50"
                      >
                        {actionLoading === 'dashboard' ? (
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-900" />
                        ) : (
                          <ArrowTopRightOnSquareIcon className="h-4 w-4" />
                        )}
                        Stripe Dashboard
                      </button>
                    </>
                  ) : (
                    <button
                      type="button"
                      onClick={handleSetupConnect}
                      disabled={actionLoading === 'setup'}
                      className="inline-flex items-center gap-2 rounded-md bg-primary-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary-500 disabled:opacity-50"
                    >
                      {actionLoading === 'setup' ? (
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                      ) : (
                        <CreditCardIcon className="h-4 w-4" />
                      )}
                      {connectStatus?.accountId ? 'Complete Setup' : 'Set Up Stripe Connect'}
                    </button>
                  )}
                </div>
              </div>

              {/* Status Details */}
              {connectStatus?.accountId && (
                <div className="mt-6 border-t border-gray-200 pt-4">
                  <dl className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                    <div>
                      <dt className="text-sm font-medium text-gray-500">Charges Enabled</dt>
                      <dd className="mt-1 text-sm text-gray-900">
                        {connectStatus.chargesEnabled ? 'Yes' : 'No'}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-sm font-medium text-gray-500">Payouts Enabled</dt>
                      <dd className="mt-1 text-sm text-gray-900">
                        {connectStatus.payoutsEnabled ? 'Yes' : 'No'}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-sm font-medium text-gray-500">Onboarding Complete</dt>
                      <dd className="mt-1 text-sm text-gray-900">
                        {connectStatus.onboardingComplete ? 'Yes' : 'No'}
                      </dd>
                    </div>
                  </dl>
                </div>
              )}
            </div>
          </div>

          {/* Create Invoice Form */}
          {showInvoiceForm && connectStatus?.chargesEnabled && (
            <div className="mb-8 bg-white shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-medium leading-6 text-gray-900">
                    Create Invoice for Client
                  </h3>
                  <button
                    type="button"
                    onClick={() => setShowInvoiceForm(false)}
                    className="text-gray-400 hover:text-gray-500"
                  >
                    <XCircleIcon className="h-5 w-5" />
                  </button>
                </div>

                <div className="space-y-4">
                  {/* Payment Type Selector */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Payment Type
                    </label>
                    <div className="grid grid-cols-2 gap-3">
                      <button
                        type="button"
                        onClick={() => setPaymentType('one_time')}
                        className={`py-3 px-4 rounded-lg border-2 text-sm font-medium transition-colors ${
                          paymentType === 'one_time'
                            ? 'border-primary-600 bg-primary-50 text-primary-700'
                            : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        <div className="text-center">
                          <div className="font-semibold">One-Time</div>
                          <div className="text-xs text-gray-500 mt-1">Single payment</div>
                        </div>
                      </button>
                      <button
                        type="button"
                        onClick={() => setPaymentType('subscription')}
                        className={`py-3 px-4 rounded-lg border-2 text-sm font-medium transition-colors ${
                          paymentType === 'subscription'
                            ? 'border-primary-600 bg-primary-50 text-primary-700'
                            : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        <div className="text-center">
                          <div className="font-semibold">Subscription</div>
                          <div className="text-xs text-gray-500 mt-1">Recurring billing</div>
                        </div>
                      </button>
                    </div>
                  </div>

                  <div>
                    <label htmlFor="client-select" className="block text-sm font-medium text-gray-700">
                      Select Client
                    </label>
                    <select
                      id="client-select"
                      value={selectedBusinessId}
                      onChange={(e) => setSelectedBusinessId(e.target.value)}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                    >
                      <option value="">Choose a client...</option>
                      {businesses.map((business) => (
                        <option key={business.id} value={business.id}>
                          {business.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label htmlFor="invoice-amount" className="block text-sm font-medium text-gray-700">
                      Amount (USD){paymentType === 'subscription' && ` per ${billingInterval}`}
                    </label>
                    <div className="mt-1 relative rounded-md shadow-sm">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <span className="text-gray-500 sm:text-sm">$</span>
                      </div>
                      <input
                        type="number"
                        id="invoice-amount"
                        value={invoiceAmount}
                        onChange={(e) => setInvoiceAmount(e.target.value)}
                        placeholder="0.00"
                        step="0.01"
                        min="0.50"
                        className="block w-full pl-7 pr-12 rounded-md border-gray-300 focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                      />
                      <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                        <span className="text-gray-500 sm:text-sm">USD</span>
                      </div>
                    </div>
                    <p className="mt-1 text-xs text-gray-500">Minimum amount is $0.50</p>
                  </div>

                  {/* Billing Interval - Only shown for subscriptions */}
                  {paymentType === 'subscription' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Billing Interval
                      </label>
                      <div className="grid grid-cols-2 gap-3">
                        <button
                          type="button"
                          onClick={() => setBillingInterval('month')}
                          className={`py-2 px-4 rounded-md border text-sm font-medium transition-colors ${
                            billingInterval === 'month'
                              ? 'border-primary-600 bg-primary-50 text-primary-700'
                              : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
                          }`}
                        >
                          Monthly
                        </button>
                        <button
                          type="button"
                          onClick={() => setBillingInterval('year')}
                          className={`py-2 px-4 rounded-md border text-sm font-medium transition-colors ${
                            billingInterval === 'year'
                              ? 'border-primary-600 bg-primary-50 text-primary-700'
                              : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
                          }`}
                        >
                          Yearly
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Description - Only for one-time payments */}
                  {paymentType === 'one_time' && (
                    <div>
                      <label htmlFor="invoice-description" className="block text-sm font-medium text-gray-700">
                        Description
                      </label>
                      <input
                        type="text"
                        id="invoice-description"
                        value={invoiceDescription}
                        onChange={(e) => setInvoiceDescription(e.target.value)}
                        placeholder="e.g., Setup fee for AI Receptionist"
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                      />
                    </div>
                  )}

                  {/* Fee info and client portal note */}
                  <div className="rounded-md bg-blue-50 p-3">
                    <p className="text-xs text-blue-700">
                      <strong>Note:</strong> This will create an invoice that the client can see and pay in their billing portal.
                      {paymentType === 'subscription' ? (
                        <> A <strong>10% platform fee</strong> will be applied to each recurring payment.</>
                      ) : (
                        <> A <strong>5% platform fee</strong> will be applied to this payment.</>
                      )}
                    </p>
                  </div>

                  <div className="flex justify-end gap-3 pt-4">
                    <button
                      type="button"
                      onClick={() => setShowInvoiceForm(false)}
                      className="rounded-md bg-white px-4 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={handleCreateInvoice}
                      disabled={
                        actionLoading === 'invoice' ||
                        !selectedBusinessId ||
                        !invoiceAmount ||
                        (paymentType === 'one_time' && !invoiceDescription)
                      }
                      className="inline-flex items-center gap-2 rounded-md bg-primary-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {actionLoading === 'invoice' ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                          Creating...
                        </>
                      ) : (
                        'Create Invoice'
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Only show billing info if Connect is active */}
          {connectStatus?.chargesEnabled && (
            <>
              {/* Quick Stats */}
              <div className="mb-8 grid grid-cols-1 gap-5 sm:grid-cols-3">
                <div className="bg-white overflow-hidden shadow rounded-lg">
                  <div className="p-5">
                    <div className="flex items-center">
                      <div className="flex-shrink-0">
                        <BanknotesIcon className="h-6 w-6 text-gray-400" />
                      </div>
                      <div className="ml-5 w-0 flex-1">
                        <dl>
                          <dt className="text-sm font-medium text-gray-500 truncate">
                            Recent Payments
                          </dt>
                          <dd className="text-lg font-semibold text-gray-900">
                            {payments.length}
                          </dd>
                        </dl>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="bg-white overflow-hidden shadow rounded-lg">
                  <div className="p-5">
                    <div className="flex items-center">
                      <div className="flex-shrink-0">
                        <UserGroupIcon className="h-6 w-6 text-gray-400" />
                      </div>
                      <div className="ml-5 w-0 flex-1">
                        <dl>
                          <dt className="text-sm font-medium text-gray-500 truncate">
                            Active Subscriptions
                          </dt>
                          <dd className="text-lg font-semibold text-gray-900">
                            {subscriptions.filter(s => s.status === 'active').length}
                          </dd>
                        </dl>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="bg-white overflow-hidden shadow rounded-lg">
                  <div className="p-5">
                    <div className="flex items-center">
                      <div className="flex-shrink-0">
                        <CreditCardIcon className="h-6 w-6 text-gray-400" />
                      </div>
                      <div className="ml-5 w-0 flex-1">
                        <dl>
                          <dt className="text-sm font-medium text-gray-500 truncate">
                            Total Revenue
                          </dt>
                          <dd className="text-lg font-semibold text-gray-900">
                            {formatPrice(
                              payments
                                .filter(p => p.status === 'succeeded')
                                .reduce((sum, p) => sum + p.amount, 0)
                            )}
                          </dd>
                        </dl>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Recent Payments */}
              <div className="mb-8 bg-white shadow rounded-lg">
                <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
                  <h3 className="text-lg font-medium leading-6 text-gray-900">
                    Recent Payments
                  </h3>
                </div>
                <div className="overflow-hidden">
                  {payments.length === 0 ? (
                    <div className="p-6 text-center text-gray-500">
                      No payments yet
                    </div>
                  ) : (
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Date
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Description
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Amount
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Status
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {payments.map((payment) => (
                          <tr key={payment.id}>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {formatDate(payment.created)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {payment.description || 'Payment'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {formatPrice(payment.amount)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span
                                className={`inline-flex rounded-full px-2 text-xs font-semibold leading-5 ${
                                  payment.status === 'succeeded'
                                    ? 'bg-green-100 text-green-800'
                                    : payment.status === 'pending'
                                    ? 'bg-yellow-100 text-yellow-800'
                                    : 'bg-red-100 text-red-800'
                                }`}
                              >
                                {payment.status}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>

              {/* Active Subscriptions */}
              <div className="bg-white shadow rounded-lg">
                <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
                  <h3 className="text-lg font-medium leading-6 text-gray-900">
                    Client Subscriptions
                  </h3>
                </div>
                <div className="overflow-hidden">
                  {subscriptions.length === 0 ? (
                    <div className="p-6 text-center text-gray-500">
                      No subscriptions yet
                    </div>
                  ) : (
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Client
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Amount
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Interval
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Status
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Next Billing
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {subscriptions.map((sub) => (
                          <tr key={sub.id}>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {sub.metadata?.businessId || 'Client'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {formatPrice(sub.items?.data?.[0]?.price?.unit_amount || 0)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 capitalize">
                              {sub.items?.data?.[0]?.price?.recurring?.interval || 'month'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span
                                className={`inline-flex rounded-full px-2 text-xs font-semibold leading-5 ${
                                  sub.status === 'active'
                                    ? 'bg-green-100 text-green-800'
                                    : sub.status === 'trialing'
                                    ? 'bg-blue-100 text-blue-800'
                                    : sub.status === 'past_due'
                                    ? 'bg-yellow-100 text-yellow-800'
                                    : 'bg-red-100 text-red-800'
                                }`}
                              >
                                {sub.status}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {sub.current_period_end
                                ? formatDate(sub.current_period_end)
                                : 'N/A'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>
            </>
          )}

          {/* Info Box for agencies not yet connected */}
          {!connectStatus?.chargesEnabled && (
            <div className="mt-6 rounded-lg bg-blue-50 p-4">
              <div className="flex">
                <CreditCardIcon className="h-5 w-5 text-blue-400" />
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-blue-800">
                    Why Set Up Stripe Connect?
                  </h3>
                  <p className="mt-2 text-sm text-blue-700">
                    Stripe Connect allows you to charge your clients directly for using the AI receptionist service.
                    You can set your own pricing and receive payments directly to your bank account.
                    A 10% platform fee is applied to each transaction.
                  </p>
                </div>
              </div>
            </div>
          )}
        </>
      )}

    </div>
  );
}
